import { useEffect, useState } from "react";
import {
    EnvelopeSimple, CheckCircle, Bug, Question, Star, ShieldWarning, PaperPlaneRight, Warning,
} from "@phosphor-icons/react";
import LegalPageShell from "@/components/LegalPageShell";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { SUPPORT_EMAIL } from "@/constants/support";

const CATEGORIES = [
    { id: "account", icon: <Question size={16} weight="fill" />, label_key: "support.cat_account" },
    { id: "billing", icon: <Star size={16} weight="fill" />, label_key: "support.cat_billing" },
    { id: "bug", icon: <Bug size={16} weight="fill" />, label_key: "support.cat_bug" },
    { id: "safety", icon: <ShieldWarning size={16} weight="fill" />, label_key: "support.cat_safety" },
    { id: "other", icon: <EnvelopeSimple size={16} weight="fill" />, label_key: "support.cat_other" },
];

export default function Support() {
    const { user } = useAuth();
    const { t } = useI18n();
    const [category, setCategory] = useState("account");
    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");
    const [email, setEmail] = useState("");
    const [status, setStatus] = useState("idle"); // idle | sending | ok | err
    const [error, setError] = useState(null);

    useEffect(() => {
        if (user?.email) setEmail(user.email);
    }, [user?.email]);

    // Prefill subject from ?subject= query param (set by legacy supportMailto links).
    useEffect(() => {
        try {
            const params = new URLSearchParams(window.location.search);
            const s = params.get("subject");
            if (s) setSubject(s.slice(0, 200));
        } catch { /* noop */ }
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        setStatus("sending");
        setError(null);
        try {
            await api.post("/support/message", {
                category, subject: subject.trim(), message: message.trim(),
                email: email.trim() || null,
            });
            setStatus("ok");
            setSubject("");
            setMessage("");
        } catch (err) {
            setStatus("err");
            setError(err.response?.data?.detail || "Something went wrong. Please try again.");
        }
    };

    return (
        <LegalPageShell
            testId="support-page"
            kicker={t("legal.support_kicker")}
            title={t("legal.support_title")}
            sub={t("legal.support_sub")}
            icon={<EnvelopeSimple size={28} weight="fill" />}
        >
            <div className="not-prose grid gap-5 sm:grid-cols-2 mb-8">
                <div
                    data-testid="support-email-link"
                    className="rounded-2xl border border-paw-line dark:border-white/10 p-5"
                >
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-paw-forest/10 dark:bg-paw-terra/15 text-paw-forest dark:text-paw-terra grid place-items-center">
                            <EnvelopeSimple size={20} weight="fill" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-xs uppercase tracking-wider text-paw-ink2 dark:text-paw-bone/60">{t("support.email_label")}</p>
                            <p className="font-semibold text-paw-ink dark:text-paw-bone break-all" dir="ltr">{SUPPORT_EMAIL}</p>
                        </div>
                    </div>
                    <p className="mt-3 text-sm text-paw-ink2 dark:text-paw-bone/60">
                        {t("support.email_hint")}
                    </p>
                </div>

                <a
                    href="/faq"
                    data-testid="support-faq-link"
                    className="rounded-2xl border border-paw-line dark:border-white/10 hover:border-paw-forest/50 dark:hover:border-paw-terra/50 p-5 transition group"
                >
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-paw-forest/10 dark:bg-paw-terra/15 text-paw-forest dark:text-paw-terra grid place-items-center">
                            <Question size={20} weight="fill" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-xs uppercase tracking-wider text-paw-ink2 dark:text-paw-bone/60">{t("support.faq_label")}</p>
                            <p className="font-semibold text-paw-ink dark:text-paw-bone">{t("support.faq_title")}</p>
                        </div>
                    </div>
                    <p className="mt-3 text-sm text-paw-ink2 dark:text-paw-bone/60">
                        {t("support.faq_hint")}
                    </p>
                </a>
            </div>

            <h2 className="!mt-0">{t("support.form_title")}</h2>
            <p>{t("support.form_sub")}</p>

            <form onSubmit={submit} className="not-prose mt-6 space-y-5" data-testid="support-form">
                {/* Category */}
                <div data-testid="support-categories">
                    <p className="text-xs uppercase tracking-wider font-semibold text-paw-ink2 dark:text-paw-bone/60 mb-2">
                        {t("support.f_category")}
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {CATEGORIES.map((c) => (
                            <button
                                key={c.id}
                                type="button"
                                onClick={() => setCategory(c.id)}
                                aria-pressed={category === c.id}
                                data-testid={`support-cat-${c.id}`}
                                className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-medium border transition ${
                                    category === c.id
                                        ? "bg-paw-forest dark:bg-paw-terra border-transparent text-white"
                                        : "border-paw-line dark:border-white/10 text-paw-ink dark:text-paw-bone hover:border-paw-forest/50 dark:hover:border-paw-terra/50"
                                }`}
                            >
                                {c.icon}
                                <span>{t(c.label_key)}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Email (only for guests) */}
                {!user && (
                    <div>
                        <label className="text-xs uppercase tracking-wider font-semibold text-paw-ink2 dark:text-paw-bone/60 mb-1.5 block">
                            {t("support.f_email")}
                        </label>
                        <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@email.com"
                            data-testid="support-input-email"
                            className="input-soft"
                            dir="ltr"
                        />
                    </div>
                )}

                <div>
                    <label className="text-xs uppercase tracking-wider font-semibold text-paw-ink2 dark:text-paw-bone/60 mb-1.5 block">
                        {t("support.f_subject")}
                    </label>
                    <input
                        type="text"
                        required
                        maxLength={200}
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder={t("support.f_subject_ph")}
                        data-testid="support-input-subject"
                        className="input-soft"
                    />
                </div>

                <div>
                    <label className="text-xs uppercase tracking-wider font-semibold text-paw-ink2 dark:text-paw-bone/60 mb-1.5 block">
                        {t("support.f_message")}
                    </label>
                    <textarea
                        required
                        rows={6}
                        minLength={10}
                        maxLength={5000}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder={t("support.f_message_ph")}
                        data-testid="support-input-message"
                        className="input-soft min-h-[140px] resize-y"
                    />
                    <p className="mt-1 text-[11px] text-paw-ink2 dark:text-paw-bone/50">
                        {message.length}/5000
                    </p>
                </div>

                <div className="flex flex-wrap items-center gap-3 pt-2">
                    <button
                        type="submit"
                        disabled={status === "sending" || subject.trim().length < 3 || message.trim().length < 10}
                        data-testid="support-submit"
                        className="btn-primary"
                    >
                        <PaperPlaneRight size={16} weight="fill" />
                        <span className="ms-1.5">
                            {status === "sending" ? t("support.sending") : t("support.send")}
                        </span>
                    </button>
                    {status === "ok" && (
                        <span
                            data-testid="support-alert-ok"
                            className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-600 dark:text-emerald-400"
                        >
                            <CheckCircle size={16} weight="fill" /> {t("support.sent_ok")}
                        </span>
                    )}
                    {status === "err" && (
                        <span
                            data-testid="support-alert-err"
                            className="inline-flex items-center gap-1.5 text-sm font-semibold text-red-600 dark:text-red-400"
                        >
                            <Warning size={16} weight="fill" /> {error}
                        </span>
                    )}
                </div>
            </form>
        </LegalPageShell>
    );
}
